import type {
    ActiveProductByExpirationItem,
    ActiveProductsByExpirationMonth,
} from 'src/redux/shopping_cart/shopping_cart.initial';

/** Solo estos `name_product` del API se muestran; el resto se ignora. */
const ALLOWED_NAME_PRODUCT_TO_UI_LABEL: Record<string, string> = {
    'Slot destacado': 'Primes',
    Slot: 'Destacados',
    Elite: 'Elite',
    Premium: 'Premium',
};

const DISPLAY_ORDER = ['Primes', 'Destacados', 'Elite', 'Premium'] as const;

/**
 * Etiqueta de UI si `name_product` (tras trim) es uno de los cuatro permitidos; si no, null (no pintar).
 */
export function uiLabelForExpirationNameProduct(nameProduct: string | null | undefined): string | null {
    if (nameProduct == null) return null;
    const key = nameProduct.trim();
    return ALLOWED_NAME_PRODUCT_TO_UI_LABEL[key] ?? null;
}

export function formatExpirationMonthKey(monthKey: string): string {
    const [ys, ms] = monthKey.split('-');
    const y = Number(ys);
    const m = Number(ms);
    if (!y || !m || m < 1 || m > 12) return monthKey;
    return new Date(y, m - 1, 1).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
}

export interface ExpirationMonthSummary {
    monthKey: string;
    monthLabel: string;
    rows: { label: string; count: number }[];
}

/**
 * Por cada clave `YYYY-MM` del objeto del API, cuenta ítems solo si `name_product` ∈ los cuatro permitidos.
 */
export function summarizeActiveProductsByExpiration(
    data: ActiveProductsByExpirationMonth | undefined | null
): ExpirationMonthSummary[] {
    if (!data || typeof data !== 'object') return [];

    return Object.keys(data)
        .sort((a, b) => a.localeCompare(b, 'es-MX'))
        .map((monthKey) => {
            const items: ActiveProductByExpirationItem[] = data[monthKey] ?? [];
            const counts: Record<string, number> = {};

            for (const item of items) {
                const label = uiLabelForExpirationNameProduct(item.name_product);
                if (!label) continue;
                counts[label] = (counts[label] ?? 0) + 1;
            }

            const rows = DISPLAY_ORDER.filter((l) => (counts[l] ?? 0) > 0).map((l) => ({
                label: l,
                count: counts[l] ?? 0,
            }));

            return {
                monthKey,
                monthLabel: formatExpirationMonthKey(monthKey),
                rows,
            };
        })
        .filter((m) => m.rows.length > 0);
}
